import { useCallback } from 'react';
import { getFlightRoomById, useSocketEvent } from '@connect-x-app/hybrid-data-access';
import {
  Channel,
  ChatChatroomJoined,
  ChatChatroomLeft,
  ChatParticipantActivated,
  ChatParticipantDeactivated,
  ChatroomAccessDeclined,
  ChatroomAccessGranted,
  ChatroomAccessRequested,
  FlightRoomUser,
  FlightRoomUserStatus,
  QueryKeys,
} from '@connect-x-app/shared-domain-models';
import { getFlightRoomStatus } from '@connect-x-app/shared-utils';
import { useQueryClient } from '@tanstack/react-query';

import {
  RoomChannels,
  useAuthenticationStore,
  useChannelSessionStore,
  useChannelStore,
  useFlightRoomSessionStore,
  useFlightRoomStore,
  useSocketStore,
  useUsersStore,
} from '../../stores';

const upsertUser = (users: FlightRoomUser[], participant: FlightRoomUser): FlightRoomUser[] =>
  users.some(u => u.externalId === participant.externalId)
    ? users.map(u => (u.externalId === participant.externalId ? participant : u))
    : [...users, participant];

const upsertParticipantInChannels = (
  chatroomId: string,
  channelIds: string[],
  participant: FlightRoomUser,
  channels: RoomChannels,
  updatePartialMainChannel: (chatroomId: string, partialChannel: Partial<Channel>) => void,
  updatePartialCustomChannel: (chatroomId: string, channelId: string, partialChannel: Partial<Channel>) => void,
) => {
  updatePartialMainChannel(chatroomId, {
    users: upsertUser(channels.mainChannel.users, participant),
  });

  channelIds.forEach(channelId => {
    const customChannel = channels.customChannels.find(c => c.id === channelId);
    if (!customChannel) {
      return;
    }
    updatePartialCustomChannel(chatroomId, channelId, {
      users: upsertUser(customChannel.users, participant),
    });
  });
};

const removeParticipantFromChannels = (
  chatroomId: string,
  participant: FlightRoomUser,
  channels: RoomChannels,
  updatePartialMainChannel: (chatroomId: string, partialChannel: Partial<Channel>) => void,
  updatePartialCustomChannel: (chatroomId: string, channelId: string, partialChannel: Partial<Channel>) => void,
) => {
  updatePartialMainChannel(chatroomId, {
    users: channels.mainChannel.users.filter(u => u.externalId !== participant.externalId),
  });
  channels.customChannels.forEach(customChannel => {
    updatePartialCustomChannel(chatroomId, customChannel.id, {
      users: customChannel.users.filter(u => u.externalId !== participant.externalId),
    });
  });
};

export const useChatChatroomSocketEvents = () => {
  const { socket } = useSocketStore();
  const { user } = useAuthenticationStore();
  const { channelsMap, setChannels, updatePartialCustomChannel, updatePartialMainChannel } = useChannelStore();
  const { flightRooms, removeFlightRoom, updateFlightRoom, updatePartialFlightRoom } = useFlightRoomStore();
  const { selectedFlightRoomId, setSelectedFlightRoomId } = useFlightRoomSessionStore();
  const { setSelectedChannelId } = useChannelSessionStore();
  const { updateUsers } = useUsersStore();
  const queryClient = useQueryClient();

  const handleChatChatroomJoined = useCallback(
    ({ channelIds, chatroomId, participant }: ChatChatroomJoined) => {
      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }
      const joinedParticipant = { ...participant, status: FlightRoomUserStatus.JOINED };
      upsertParticipantInChannels(
        chatroomId,
        channelIds,
        joinedParticipant,
        channels,
        updatePartialMainChannel,
        updatePartialCustomChannel,
      );
      updateUsers([joinedParticipant]);
    },
    [channelsMap, updatePartialCustomChannel, updatePartialMainChannel, updateUsers],
  );

  const handleAccessRequested = useCallback(
    ({ chatroomId, participant }: ChatroomAccessRequested) => {
      const room = flightRooms.get(chatroomId);

      if (!room && participant.externalId === user?.externalId) {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.FLIGHTROOMS] });
        return;
      }

      if (room) {
        updatePartialFlightRoom(chatroomId, {
          accessRequestCount: (room.accessRequestCount ?? 0) + 1,
        });
      }

      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }

      updatePartialMainChannel(chatroomId, {
        users: upsertUser(channels.mainChannel.users, { ...participant, status: FlightRoomUserStatus.PENDING }),
      });
    },
    [channelsMap, flightRooms, queryClient, updatePartialFlightRoom, updatePartialMainChannel, user],
  );

  const handleAccessGranted = useCallback(
    ({ channelIds, chatroomId, userId }: ChatroomAccessGranted) => {
      const room = flightRooms.get(chatroomId);
      if (room) {
        updatePartialFlightRoom(chatroomId, {
          accessRequestCount: Math.max(0, (room.accessRequestCount ?? 0) - 1),
        });
      }

      if (userId === user?.externalId) {
        updatePartialFlightRoom(chatroomId, { userStatus: FlightRoomUserStatus.JOINED });
        return;
      }

      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }
      const pendingUser = channels.mainChannel.users.find(u => u.externalId === userId);
      if (!pendingUser) {
        return;
      }

      upsertParticipantInChannels(
        chatroomId,
        channelIds,
        { ...pendingUser, status: FlightRoomUserStatus.JOINED },
        channels,
        updatePartialMainChannel,
        updatePartialCustomChannel,
      );
    },
    [channelsMap, flightRooms, updatePartialCustomChannel, updatePartialFlightRoom, updatePartialMainChannel, user],
  );

  const handleAccessDeclined = useCallback(
    ({ chatroomId, userId }: ChatroomAccessDeclined) => {
      if (userId === user?.externalId) {
        removeFlightRoom(chatroomId);
        return;
      }

      const room = flightRooms.get(chatroomId);
      if (room) {
        updatePartialFlightRoom(chatroomId, {
          accessRequestCount: Math.max(0, (room.accessRequestCount ?? 0) - 1),
        });
      }

      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }

      updatePartialMainChannel(chatroomId, {
        users: channels.mainChannel.users.filter(u => u.externalId !== userId),
      });
    },
    [channelsMap, flightRooms, removeFlightRoom, updatePartialFlightRoom, updatePartialMainChannel, user],
  );

  const handleParticipantRemoved = useCallback(
    ({ chatroomId, participant }: ChatChatroomLeft | ChatParticipantDeactivated) => {
      if (participant.externalId === user?.externalId) {
        removeFlightRoom(chatroomId);
        if (selectedFlightRoomId === chatroomId) {
          setSelectedFlightRoomId(null);
          setSelectedChannelId(null);
        }
        return;
      }

      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }
      removeParticipantFromChannels(chatroomId, participant, channels, updatePartialMainChannel, updatePartialCustomChannel);
    },
    [
      channelsMap,
      removeFlightRoom,
      selectedFlightRoomId,
      setSelectedChannelId,
      setSelectedFlightRoomId,
      updatePartialCustomChannel,
      updatePartialMainChannel,
      user?.externalId,
    ],
  );

  const handleParticipantActivated = useCallback(
    async ({ channelIds, chatroomId, participant }: ChatParticipantActivated) => {
      if (participant.externalId === user?.externalId) {
        const room = await getFlightRoomById(chatroomId);
        const { customChannels, mainChannel, ...roomSummary } = room;
        updateFlightRoom({ ...roomSummary, status: getFlightRoomStatus(room) });
        if (customChannels && mainChannel && (!channelsMap.get(chatroomId) || selectedFlightRoomId !== chatroomId)) {
          setChannels(chatroomId, { customChannels, mainChannel });
        }
        return;
      }

      const channels = channelsMap.get(chatroomId);
      if (!channels) {
        return;
      }
      upsertParticipantInChannels(
        chatroomId,
        channelIds,
        { ...participant, status: FlightRoomUserStatus.SCHEDULED },
        channels,
        updatePartialMainChannel,
        updatePartialCustomChannel,
      );
    },
    [
      channelsMap,
      selectedFlightRoomId,
      setChannels,
      updateFlightRoom,
      updatePartialCustomChannel,
      updatePartialMainChannel,
      user,
    ],
  );

  useSocketEvent(socket, 'chat.chatroom-joined', handleChatChatroomJoined);
  useSocketEvent(socket, 'chat.chatroom-access-requested', handleAccessRequested);
  useSocketEvent(socket, 'chat.chatroom-access-granted', handleAccessGranted);
  useSocketEvent(socket, 'chat.chatroom-access-declined', handleAccessDeclined);
  useSocketEvent(socket, 'chat.chatroom-left', handleParticipantRemoved);
  useSocketEvent(socket, 'chat.participant-activated', handleParticipantActivated);
  useSocketEvent(socket, 'chat.participant-deactivated', handleParticipantRemoved);
};
